import { createContext, type FC, type PropsWithChildren, useContext, useMemo, useRef } from 'react'

export interface ActiveVoiceRecorder {
  discard: () => void
  stopIntoPreview: () => void
}

export interface VoicePlayback {
  claim: (id: string, stop: () => void) => void
  release: (id: string) => void
  stopAll: () => void
  claimRecorder: (id: string, recorder: ActiveVoiceRecorder) => void
  releaseRecorder: (id: string) => void
}

const noop = () => {}

const VoicePlaybackContext = createContext<VoicePlayback>({
  claim: noop,
  release: noop,
  stopAll: noop,
  claimRecorder: noop,
  releaseRecorder: noop,
})

export const VoicePlaybackProvider: FC<PropsWithChildren> = ({ children }) => {
  const current = useRef<{ id: string; stop: () => void } | null>(null)
  const recorder = useRef<({ id: string } & ActiveVoiceRecorder) | null>(null)

  const value = useMemo<VoicePlayback>(
    () => ({
      claim: (id, stop) => {
        // A clip never plays over a recording: the speaker would end up in the clip.
        recorder.current?.stopIntoPreview()
        if (current.current && current.current.id !== id) {
          current.current.stop()
        }
        current.current = { id, stop }
      },
      release: (id) => {
        if (current.current?.id === id) {
          current.current = null
        }
      },
      stopAll: () => {
        current.current?.stop()
        current.current = null
      },
      claimRecorder: (id, active) => {
        if (recorder.current && recorder.current.id !== id) {
          recorder.current.discard()
        }
        recorder.current = { id, ...active }
      },
      releaseRecorder: (id) => {
        if (recorder.current?.id === id) {
          recorder.current = null
        }
      },
    }),
    []
  )

  return <VoicePlaybackContext.Provider value={value}>{children}</VoicePlaybackContext.Provider>
}

export const useVoicePlayback = () => useContext(VoicePlaybackContext)
